/**
 * What counts as "not in a Project", in one place.
 *
 * ── Why this module exists ───────────────────────────────────────────────
 * Two screens answer the same question and they used to answer it with two
 * independent copies of the rules:
 *
 *   • `ProjectsComponent.orphanBookCount / orphanDraftCount` drive the
 *     "Items are not in a Project" banner.
 *   • `OrganizeProjectsComponent.analyze()` decides which rows enter the
 *     analysis pool that the Organize Projects dialog offers to file.
 *
 * The copies had drifted. The Book path filtered by Organization, archived
 * and proof in both places, but the Draft path filtered by nothing at all on
 * the banner side, and `analyze()` carried a comment asserting that "Drafts
 * are not org-scoped by `org_ID` on the model" — which is simply not true,
 * `Draft.org_ID` is declared on the model. A Draft left in the cache from a
 * previously-viewed Organization was therefore counted by the banner as work
 * the operator had to do.
 *
 * Any drift of that kind produces the same user-visible failure: the banner
 * nags about N items, the operator opens Organize Projects on the strength of
 * that nag, and the dialog has nothing to show them. So the rules live here
 * and both callers ask the same functions.
 *
 * ── The rules ────────────────────────────────────────────────────────────
 * A row is an organizable orphan when all of these hold:
 *
 *   1. It has no Project. `work_ID` null / undefined / 0 means unfiled.
 *   2. It belongs to the Organization currently in view. The caches survive
 *      an org or role switch, and filing a row into a Project in another
 *      Organization is never valid.
 *   3. It is something the operator could actually file. Archived Books are
 *      retired on purpose and only linger so historical Orders and Packages
 *      still resolve; proof Books are one-off physical samples meant to be
 *      consumed, not catalogued. Neither belongs in a Project, so neither
 *      should be counted as outstanding work. Drafts have no equivalent
 *      state.
 */

/**
 * Stringly-typed `Book.archived` sentinel.
 *
 * Any case-insensitive `'TRUE'` is archived; everything else — NULL, '',
 * 'FALSE', undefined — is active.
 */
export function isArchivedBook(book: any): boolean {
  if (!book) { return false; }
  return String(book.archived || '').toUpperCase() === 'TRUE';
}

/**
 * Stringly-typed `Book.isProof` sentinel.
 *
 * A peer of `isArchivedBook` rather than folded into it, so the two states
 * stay independently observable: a Book can be a proof without being
 * archived and vice versa.
 */
export function isProofBook(book: any): boolean {
  if (!book) { return false; }
  return String(book.isProof || '').toUpperCase() === 'TRUE';
}

/**
 * Is `row` in the Organization currently in view?
 *
 * Deliberately permissive in both directions. When the caller does not know
 * the current Organization, or the row carries no `org_ID`, the row is kept:
 * excluding on missing data would hide real work if a server build ever
 * stopped sending the field. Only a row that positively declares a different
 * Organization is rejected.
 */
export function belongsToOrganization(row: any, currentOrgId: any): boolean {
  if (!row) { return false; }
  if (!currentOrgId) { return true; }
  const rowOrgId = row.org_ID;
  if (!rowOrgId) { return true; }
  return Number(rowOrgId) === Number(currentOrgId);
}

/** Unfiled: `work_ID` is null, undefined, 0 or negative. */
export function isUnassigned(row: any): boolean {
  if (!row) { return false; }
  const w = row.work_ID;
  return !w || Number(w) <= 0;
}

/**
 * Could this Book be offered by Organize Projects, whether or not it is
 * currently filed? The eligibility half of the orphan test, kept separate
 * because `analyze()` needs the whole eligible pool — assigned rows included,
 * since an assigned sibling is what lets an orphan be recognised as belonging
 * to that sibling's Project.
 */
export function isOrganizableBook(book: any, currentOrgId: any): boolean {
  if (!book) { return false; }
  if (!belongsToOrganization(book, currentOrgId)) { return false; }
  if (isArchivedBook(book)) { return false; }
  if (isProofBook(book)) { return false; }
  return true;
}

/** As `isOrganizableBook`, for Drafts. Drafts have no archived / proof state. */
export function isOrganizableDraft(draft: any, currentOrgId: any): boolean {
  if (!draft) { return false; }
  return belongsToOrganization(draft, currentOrgId);
}

/** A Book the banner should count and the dialog must be able to offer. */
export function isOrphanBook(book: any, currentOrgId: any): boolean {
  return isOrganizableBook(book, currentOrgId) && isUnassigned(book);
}

/** A Draft the banner should count and the dialog must be able to offer. */
export function isOrphanDraft(draft: any, currentOrgId: any): boolean {
  return isOrganizableDraft(draft, currentOrgId) && isUnassigned(draft);
}

/** Count of orphan Books in `books` for `currentOrgId`. */
export function countOrphanBooks(books: any[], currentOrgId: any): number {
  let n = 0;
  for (const b of (books || [])) {
    if (isOrphanBook(b, currentOrgId)) { n++; }
  }
  return n;
}

/** Count of orphan Drafts in `drafts` for `currentOrgId`. */
export function countOrphanDrafts(drafts: any[], currentOrgId: any): number {
  let n = 0;
  for (const d of (drafts || [])) {
    if (isOrphanDraft(d, currentOrgId)) { n++; }
  }
  return n;
}

